import type { BrandConfig } from '@/config/brands/types';

export interface ResolveHeadBrandInput {
  /** Validated partner `redirect_url` — present only on the sign-in re-skin hop. */
  redirectUrl: string | null;
  /**
   * Whether the current route is an auth surface whose page BODY re-skins to the
   * partner brand (sign-in / invitation-accept). The head override is scoped to
   * these routes so a stray `redirect_url` on an ordinary app route can't flip the
   * tab title/meta while the body still shows the primary brand.
   */
  isReskinRoute: boolean;
  /**
   * Brand resolved from the sign-in redirect. Equals `base` (by reference) when
   * there is no partner redirect, or the redirect targets the primary itself.
   */
  signInBrand: BrandConfig;
  /** Ambient effective brand for the current host/route. */
  ambient: BrandConfig;
  /** Whether the ambient brand's DB overlay has settled. */
  ambientReady: boolean;
  /** The primary `brandingConfig` sentinel `useResolveSignInBrand` returns for "no partner". */
  base: BrandConfig;
}

export interface HeadBrand {
  brand: BrandConfig;
  isReady: boolean;
}

/**
 * Pick the brand that drives the document head (`<title>` + meta description).
 *
 * On the sign-in re-skin HOP — we are on a re-skin route (`isReskinRoute`) AND a
 * partner `redirect_url` is present AND the sign-in resolver returned a brand other
 * than the primary `base` — the partner brand wins, so the browser tab matches the
 * visible partner branding (never "TagadaPay"). Off-hop (or off the re-skin routes,
 * where the body is not re-skinned), the ambient brand drives the head.
 *
 * The sign-in brand is already resolved inside `useResolveSignInBrand` (it returns
 * a blank neutral brand while loading), so the hop's readiness is decoupled from
 * the ambient readiness signal — the consumer's `appName` guard defers stamping a
 * blank name until the real partner brand lands.
 */
export const resolveHeadBrand = ({
  redirectUrl,
  isReskinRoute,
  signInBrand,
  ambient,
  ambientReady,
  base,
}: ResolveHeadBrandInput): HeadBrand => {
  const signInOverride = isReskinRoute && redirectUrl && signInBrand !== base ? signInBrand : null;
  return {
    brand: signInOverride ?? ambient,
    isReady: signInOverride ? true : ambientReady,
  };
};
